import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

// --------- API contracts ---------

export interface JoinedLog {
  thing_name: string;
  timestamp: number;

  // device_maintenance_log
  pcb: string;                 pcb_timestamp: number;
  antenna: string;             antenna_timestamp: number;
  xbee: string;                xbee_timestamp: number;
  sim: string;                 sim_timestamp: number;
  device_case: string;         device_case_timestamp: number;
  cover: string;               cover_timestamp: number;
  bracket: string;             bracket_timestamp: number;
  dml_user_sub: string; // aliased in PHP (device_maintenance_log.user_sub)

  // xbee_config_log
  network_technology: string;              network_technology_timestamp: number;
  access_point: string;                    access_point_timestamp: number;
  airplane_mode: string;                   airplane_mode_timestamp: number;
  carrier: string;                         carrier_timestamp: number;
  api_enable: string;                      api_enable_timestamp: number;
  baud_rate: string;                       baud_timestamp: number;
  packetization_timeout: string;           packetization_timeout_timestamp: number;
  flow_control: string;                    flow_control_timestamp: number;
  parity: string;                          parity_timestamp: number;
  stop_bits: string;                       stop_bits_timestamp: number;

  DIO0: string;  DIO0_timestamp: number;
  DIO1: string;  DIO1_timestamp: number;
  DIO2: string;  DIO2_timestamp: number;
  DIO3: string;  DIO3_timestamp: number;
  DIO4: string;  DIO4_timestamp: number;
  DIO5: string;  DIO5_timestamp: number;
  DIO6: string;  DIO6_timestamp: number;
  DIO7: string;  DIO7_timestamp: number;
  DIO8: string;  DIO8_timestamp: number;
  DIO9: string;  DIO9_timestamp: number;
  DIO10: string; DIO10_timestamp: number;
  DIO11: string; DIO11_timestamp: number;
  DIO12: string; DIO12_timestamp: number;
  DIO13: string; DIO13_timestamp: number;
  DIO14: string; DIO14_timestamp: number;

  pr: string;   pr_timestamp: number;
  pd: string;   pd_timestamp: number;
  m0: string;   m0_timestamp: number;
  m1: string;   m1_timestamp: number;

  xcl_user_sub: string; // aliased in PHP (xbee_config_log.user_sub)
}

/** Full create payload – explicit, complete mirror of both tables. */
export interface CreateMaintenancePayload {
  // shared
  thing_name: string;
  timestamp: number;
  user_sub_dml: string; // device_maintenance_log.user_sub
  user_sub_xcl: string; // xbee_config_log.user_sub

  // device_maintenance_log (all required by your create-module usage)
  pcb: string;                 pcb_timestamp: number;
  antenna: string;             antenna_timestamp: number;
  xbee: string;                xbee_timestamp: number;
  sim: string;                 sim_timestamp: number;
  device_case: string;         device_case_timestamp: number;
  cover: string;               cover_timestamp: number;
  bracket: string;             bracket_timestamp: number;

  // xbee_config_log (all required by your create-module usage)
  network_technology: string;              network_technology_timestamp: number;
  access_point: string;                    access_point_timestamp: number;
  airplane_mode: string;                   airplane_mode_timestamp: number;
  carrier: string;                         carrier_timestamp: number;
  api_enable: string;                      api_enable_timestamp: number;
  baud_rate: string;                       baud_timestamp: number;
  packetization_timeout: string;           packetization_timeout_timestamp: number;
  flow_control: string;                    flow_control_timestamp: number;
  parity: string;                          parity_timestamp: number;
  stop_bits: string;                       stop_bits_timestamp: number;

  DIO0: string;  DIO0_timestamp: number;
  DIO1: string;  DIO1_timestamp: number;
  DIO2: string;  DIO2_timestamp: number;
  DIO3: string;  DIO3_timestamp: number;
  DIO4: string;  DIO4_timestamp: number;
  DIO5: string;  DIO5_timestamp: number;
  DIO6: string;  DIO6_timestamp: number;
  DIO7: string;  DIO7_timestamp: number;
  DIO8: string;  DIO8_timestamp: number;
  DIO9: string;  DIO9_timestamp: number;
  DIO10: string; DIO10_timestamp: number;
  DIO11: string; DIO11_timestamp: number;
  DIO12: string; DIO12_timestamp: number;
  DIO13: string; DIO13_timestamp: number;
  DIO14: string; DIO14_timestamp: number;

  pr: string;   pr_timestamp: number;
  pd: string;   pd_timestamp: number;
  m0: string;   m0_timestamp: number;
  m1: string;   m1_timestamp: number;
}

// --------- endpoints (put these in environment.api.route) ---------
const routes = {
  create: environment.api.stage + environment.api.route.createMaintenanceLog,
  latestByThing: environment.api.stage + environment.api.route.getLatestLogByThingName,
  allByThing: environment.api.stage + environment.api.route.getAllLogsByThingName,
  latestAll: environment.api.stage + environment.api.route.getLatestLogForAllThings,
  getAllDevices: environment.api.stage + environment.api.route.getAllDevices
};

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  constructor(private http: HttpClient) {}

  create(payload: CreateMaintenancePayload): Observable<any> {
    return this.http.post(routes.create, payload, { headers: this.headers });
  }

  getLatestByThingName(thing_name: string): Observable<JoinedLog[]> {
    const params = new HttpParams().set('thing_name', thing_name);
    return this.http.get<any>(routes.latestByThing, { params }).pipe(map(this.normalizeArray));
  }

  getAllByThingName(thing_name: string): Observable<JoinedLog[]> {
    const params = new HttpParams().set('thing_name', thing_name);
    return this.http.get<any>(routes.allByThing, { params }).pipe(map(this.normalizeArray));
  }

  getLatestForAllThings(): Observable<JoinedLog[]> {
    return this.http.get<any>(routes.latestAll).pipe(map(this.normalizeArray));
  }

  getDevices(){
    return firstValueFrom(this.http.get<any>(routes.getAllDevices));
  }

  // --- helpers ---
  private normalizeArray = (res: any): JoinedLog[] => {
    const arr: any[] = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : [res]);
    // coerce number-like strings to numbers for timestamps
    for (const r of arr) {
      Object.keys(r).forEach(k => {
        if (k === 'timestamp' || /_timestamp$/.test(k)) {
          const v = r[k];
          if (typeof v === 'string' && /^\d+$/.test(v)) r[k] = Number(v);
        }
      });
    }
    return arr as JoinedLog[];
  };
}
